import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Spin, App, Tabs } from "antd";
import BackButton from "../../components/CustomButton/BackButton";
import CustomCard from "../../components/CustomCard/CustomCard";
import DetailsHeader from "../../components/Details/DetailsHeader";
import Detail from "../../components/Details/Detail";
import { busEntDetailedView } from "../../redux/slices/creditLimitSlice";
import {
  TITLE_TXT,
  LABEL_TXT,
  ENT_STATUS,
} from "../../constants/constants";
import { convertToPercentage, formatCurrency, formatDiscountRatePercent, formatMaxFundRequiredPercent, discountRateRules } from "../../helpers/helpers";
import { getDealCategoryDisplayName } from "../../helpers/dealCategoryProducts";
import { PERM_CODE } from "../../constants/PermissionCodes";
import CreditLimitActivityLog from "./Components/CreditLimitActivityLog.jsx";

const BusEntDetailedView = () => {
  const dispatch = useDispatch();
  const { busEntId, status } = useParams();
  const { notification } = App.useApp();
  const { busEntDetailedData, loading, error, editBusEntSaveLoading } =
    useSelector((state) => state.creditLimit);
  const { user } = useSelector((state) => state.auth);

  const userPrivileges = useMemo(() => {
    const raw = user?.privilege || [];
    return raw
      .map((p) => (typeof p === "string" ? p : p?.privilegeCode))
      .filter(Boolean);
  }, [user]);

  const rowStatus = busEntDetailedData?.busEntStatusId || status;

  const editAccess =
    (userPrivileges.includes(PERM_CODE.CPEDT) &&
      rowStatus === ENT_STATUS.PENDING.ID) ||
    (userPrivileges.includes(PERM_CODE.CVEDT) &&
      rowStatus === ENT_STATUS.ACTIVE.ID) ||
    (userPrivileges.includes(PERM_CODE.CDEDT) &&
      rowStatus === ENT_STATUS.DEACTIVATED.ID) ||
    (userPrivileges.includes(PERM_CODE.CREDT) &&
      rowStatus === ENT_STATUS.REJECTED.ID);


  useEffect(() => {
    dispatch(busEntDetailedView(busEntId));
  }, [dispatch, busEntId]);

  useEffect(() => {
    if (error) {
      notification.error({
        message: "Error",
        description: error,
        placement: "topRight",
      });
    }
  }, [error, notification]);

  const {
    entName,
    orgName,
    dealCategoryId,
    dealCategoryName,
    previousCredit,
    approvedCredit,
    annualDiscountRate,
    maxFundRequiredPct,
    kycDetails,
    comments,
    PlatformFee,
  } = busEntDetailedData || {};

  const sanction_documents = kycDetails?.sanction_documents?.docUrl;
  const {
    platformFeeId,
    platformFee,
    stampFee,
    miscellaneousFees,
    penalCharge,
  } = PlatformFee || {};

  return (
    <div className="bus-ent-detailed-container">
      <BackButton />
      <CustomCard>
        <DetailsHeader
          title={TITLE_TXT.BUSENT_DETS}
          name={`${entName || ""} & ${orgName || ""}`}
          id={busEntId}
          type="busEntPdf"
        />
        <Tabs
          items={[
            {
              key: "1",
              label: "Details",
              children: (
                <div className="details-card-container">
                  <div className="details-card">
                    <Spin spinning={loading || editBusEntSaveLoading}>
                      {/* Basic Details */}
                      <Detail
                        label={LABEL_TXT.DEAL_CAT}
                        value={
                          getDealCategoryDisplayName(dealCategoryId, dealCategoryName) ||
                          "N/A"
                        }
                      />
                      <Detail label={LABEL_TXT.ENT_NAME} value={entName} />
                      <Detail label={LABEL_TXT.BUS_NAME} value={orgName} />

                      {/* Editable Fields */}
                      <Detail
                        label={LABEL_TXT.PRE_CRED}
                        value={previousCredit || 0}
                        displayValue={formatCurrency(previousCredit)}
                        name="previousCredit"
                        placeholder="Enter Previous Credit"
                        rules={[{ required: true, message: "Enter Previous Credit" }]}
                        isEditable={editAccess}
                        isBusEnt={true}
                        busEntId={busEntId}
                        isNumInput={true}
                      />

                      <Detail
                        label={LABEL_TXT.APP_CRED}
                        value={`${formatCurrency(approvedCredit || 0)}`}
                      />

                      <Detail
                        label={LABEL_TXT.ANN_DIS_RATE}
                        value={formatDiscountRatePercent(annualDiscountRate)}
                        displayValue={convertToPercentage(annualDiscountRate)}
                        name="discountRate"
                        placeholder="Enter Annual Interest Rate (e.g. 7.5)"
                        rules={discountRateRules}
                        isEditable={editAccess}
                        isInput
                        isBusEnt={true}
                        busEntId={busEntId}
                      />
                      <Detail
                        label={LABEL_TXT.MAX_FUND_REQ_PCT}
                        value={formatMaxFundRequiredPercent(maxFundRequiredPct ?? 0.8)}
                        displayValue={convertToPercentage(maxFundRequiredPct ?? 0.8)}
                      />
                      <Detail label={LABEL_TXT.COMMENTS} value={comments || "N/A"} />

                      {/* PDF Viewer in Popup Modal */}
                      <Detail
                        label={LABEL_TXT.SANCTION_DOCS}
                        isDownloadBtn={sanction_documents && true}
                        value={sanction_documents || "N/A"}
                      />

                      {/* Divider */}
                      <div
                        style={{
                          borderTop: "1px solid #eee",
                          margin: "20px 0",
                        }}
                      />

                      {/* Platform Fee Section */}
                      <h3 style={{ marginBottom: "16px" }}>
                        Platform Fee ID: {platformFeeId || "N/A"}
                      </h3>

                      <Detail
                        label={LABEL_TXT.PLAT_FEE_RATE}
                        value={platformFee ? `${platformFee}%` : "0%"}
                        displayValue={convertToPercentage(platformFee)}
                      />
                      <Detail label={LABEL_TXT.STAMP_FEE} value={stampFee || 0} />
                      <Detail
                        label={LABEL_TXT.MISC_FEE}
                        value={miscellaneousFees || 0}
                      />
                      <Detail
                        label={LABEL_TXT.PENAL_CHARGES}
                        value={penalCharge ? `${penalCharge}%` : "0%"}
                        displayValue={convertToPercentage(penalCharge)}
                      />
                    </Spin>
                  </div>
                </div>
              ),
            },
            {
              key: "2",
              label: "Activity Log",
              children: (
                <CreditLimitActivityLog
                  busEntId={busEntId}
                  entName={entName}
                  orgName={orgName}
                />
              ),
            },
          ]}
        />
      </CustomCard>
    </div>
  );
};

export default BusEntDetailedView;
